import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, X, Smartphone } from 'lucide-react';
import { Button } from './button';
import { useOffline } from '@/hooks/useOffline';

import { tAuto } from "@/lib/tAuto";
export function InstallPWAPrompt() {
  const { installable, install, isPWA } = useOffline();
  const [dismissed, setDismissed] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);
  const [pathname, setPathname] = useState<string>(() =>
    typeof window !== "undefined" ? window.location.pathname : "/"
  );

  // Track route changes (works for both pushState and popstate)
  useEffect(() => {
    if (typeof window === "undefined") return;
    const update = () => setPathname(window.location.pathname);
    window.addEventListener("popstate", update);
    const interval = window.setInterval(update, 500);
    return () => {
      window.removeEventListener("popstate", update);
      window.clearInterval(interval);
    };
  }, []);

  // Routes where the install banner must NEVER appear (would cover the form)
  const isBlockedRoute =
    pathname.startsWith("/auth") ||
    pathname.startsWith("/afiliado/auth") ||
    pathname.startsWith("/admin");

  // Show prompt after 5 seconds if installable
  useEffect(() => {
    if (installable && !isPWA && !dismissed && !isBlockedRoute) {
      const timer = setTimeout(() => setShowPrompt(true), 5000);
      return () => clearTimeout(timer);
    }
    // Hide immediately if we navigate into a blocked route
    if (isBlockedRoute) setShowPrompt(false);
  }, [installable, isPWA, dismissed, isBlockedRoute]);

  // Check localStorage for dismissed state
  useEffect(() => {
    const dismissedAt = localStorage.getItem('pwa-prompt-dismissed');
    if (dismissedAt) {
      const daysSince = (Date.now() - parseInt(dismissedAt)) / (1000 * 60 * 60 * 24);
      // Show again after 7 days
      if (daysSince < 7) {
        setDismissed(true);
      }
    }
  }, []);

  const handleDismiss = () => {
    setShowPrompt(false);
    setDismissed(true);
    localStorage.setItem('pwa-prompt-dismissed', Date.now().toString());
  };

  const handleInstall = async () => {
    const success = await install();
    if (success) {
      setShowPrompt(false);
    }
  };

  if (!showPrompt || isBlockedRoute) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        className="fixed bottom-0 left-0 right-0 z-[150] bg-background border-t shadow-lg"
      >
        <div className="max-w-screen-xl mx-auto px-4 py-4">
          <div className="flex items-start gap-4">
            <div className="relative w-14 h-14 bg-gradient-to-br from-primary/20 via-primary/10 to-transparent border border-primary/20 rounded-2xl flex items-center justify-center shrink-0 shadow-[inset_0_1px_2px_rgba(255,255,255,0.5)] overflow-hidden group">
              <div className="absolute inset-0 bg-gradient-to-tr from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              <Smartphone className="w-7 h-7 text-primary drop-shadow-sm" strokeWidth={1.5} />
            </div>
            
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-base">Instalar Berastays</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Adicione à tela inicial para acesso rápido e experiência de app nativo, mesmo offline.
              </p>
              
              <div className="flex gap-2 mt-3">
                <Button 
                  size="sm" 
                  onClick={handleInstall}
                  className="gap-2"
                >
                  <Download className="w-4 h-4" />
                  Instalar
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={handleDismiss}
                >
                  Agora não
                </Button>
              </div>
            </div>

            <button
              onClick={handleDismiss}
              className="p-2 hover:bg-muted rounded-full transition-colors shrink-0"
              aria-label="Fechar"
            >
              <X className="w-5 h-5 text-muted-foreground" />
            </button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

/**
 * Update available prompt
 */
export function UpdatePrompt() {
  const { updateAvailable, update } = useOffline();

  if (!updateAvailable) return null;

  return (
    <motion.div
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      className="fixed top-16 left-4 right-4 z-[150] bg-primary text-primary-foreground rounded-lg shadow-lg"
    >
      <div className="flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-3">
          <Download className="w-5 h-5" />
          <div>
            <p className="font-medium text-sm">{tAuto("auto.components_ui_installpwa.1", "Nova versão disponível")}</p>
            <p className="text-xs opacity-90">{tAuto("auto.components_ui_installpwa.2", "Atualize para obter as últimas funcionalidades")}</p>
          </div>
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={update}
          className="shrink-0"
        >
          Atualizar
        </Button>
      </div>
    </motion.div>
  );
}
